/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * File Loader Module - AJAX загрузка файлов с сервера
 * 
 * Phase 4: File Management Migration
 * Migrated from main.js lines 448-520
 */

import { unzip } from '../storage/local-storage.js';

/**
 * File Loader Class - handles loading files from server
 */
class FileLoader {
    constructor() {
        // Use versioned URL from cache manifest if available
        const baseUrl = '/api/files/';
        this.defaultUrl = (window.getVersionedUrl && typeof window.getVersionedUrl === 'function') 
            ? window.getVersionedUrl(baseUrl) 
            : baseUrl;
        const summariesBaseUrl = '/api/files/summaries';
        this.summaryUrl = (window.getVersionedUrl && typeof window.getVersionedUrl === 'function') 
            ? window.getVersionedUrl(summariesBaseUrl) 
            : summariesBaseUrl;
        this.loading = false;
        this.lastRequest = null;
        
        // Initialization logging only in debug mode
    }
    
    /**
     * Load files from server via AJAX
     * @param {Object} options - Loading options
     * @returns {Promise<Array>} - Promise that resolves to files array
     */
    async loadFromServer(options = {}) {
        let {
            url = this.defaultUrl,
            async = true,
            timeout = 30000,
            onProgress = null,
            onSuccess = null,
            onError = null,
            forceUpdate = false
        } = options;
        
        // CRITICAL FIX: Prevent language modules from corrupting API URLs
        if (url && url.includes('/api/')) {
            // Ensure API URLs are not modified by language routing
            url = url.replace(/^.*?(\/api\/.*)$/, '$1');
            // Ensure it starts with /api/ if it's an API endpoint
            if (!url.startsWith('/')) {
                url = '/' + url;
            }
        }
        
        // Version-based caching: version in URL ensures cache busting when data changes
        // No need for timestamp cache-busting parameter - version in URL handles it
        
        if (this.loading) {
            if (window.HyperLog) {
                window.HyperLog.warn('[FileLoader] Already loading, cancelling previous request');
            }
            if (this.lastRequest) {
                this.lastRequest.abort();
            }
        }
        
        this.loading = true;
        
        // Starting AJAX request logging only in debug mode
        
        return new Promise((resolve, reject) => {
            this.lastRequest = $.ajax({
                url: url,
                type: 'GET',
                async: async,
                timeout: timeout,
                contentType: 'application/json; charset=utf-8',
                dataType: 'text', // Get as text first, then parse JSON to preserve UTF-8 encoding
                xhr: () => {
                    const xhr = $.ajaxSettings.xhr();
                    // Ensure UTF-8 encoding for response
                    if (xhr.overrideMimeType) {
                        xhr.overrideMimeType('application/json; charset=utf-8');
                    }
                    // Download progress (if Content-Length provided)
                    if (onProgress && xhr) {
                        xhr.addEventListener('progress', (e) => {
                            try {
                                if (e.lengthComputable) {
                                    const percent = Math.max(0, Math.min(100, Math.round((e.loaded / Math.max(1, e.total)) * 100)));
                                    onProgress({ type: 'download', percent });
                                }
                            } catch(_){}
                        }, false);
                    }
                    return xhr;
                }
            })
            .done((response, status, xhr) => {
                this.loading = false;
                
                try {
                    // CRITICAL: Handle UTF-8 encoding for gzip-compressed responses
                    // jQuery automatically decompresses gzip, but we need to ensure UTF-8 is preserved
                    let responseToProcess = response;
                    
                    // If response is a string (not auto-parsed by jQuery), parse it directly
                    // This happens when dataType is 'text' and response is gzip-compressed
                    if (typeof response === 'string') {
                        // Response is already a string (jQuery didn't auto-parse)
                        // This is correct - we'll parse it in processResponse
                        responseToProcess = response;
                    } else if (typeof response === 'object' && xhr.responseText) {
                        // jQuery auto-parsed JSON - re-parse from raw responseText to ensure UTF-8
                        // This is important for gzip-compressed responses where encoding might be lost
                        try {
                            // xhr.responseText contains the decompressed text (jQuery handles gzip automatically)
                            responseToProcess = JSON.parse(xhr.responseText);
                        } catch(e) {
                            // If re-parsing fails, use original response
                            responseToProcess = response;
                        }
                    }
                    
                    const result = this.processResponse(responseToProcess);
                    // Detect OT access flag from JSON response (object or JSON string)
                    let hasOtAccess = false;
                    try {
                        if (response && typeof response === 'object' && typeof response.has_ot_access !== 'undefined') {
                            hasOtAccess = !!response.has_ot_access;
                        } else if (typeof response === 'string') {
                            try { const obj = JSON.parse(response); if (obj && typeof obj.has_ot_access !== 'undefined') { hasOtAccess = !!obj.has_ot_access; } } catch(_){ /* not JSON */ }
                        }
                    } catch(_) { hasOtAccess = false; }
                    
                    // Get server timestamp from Last-Modified header
                    const lastModifiedHeader = xhr.getResponseHeader('Last-Modified');
                    let serverTimestamp = Date.now(); // Fallback to current time
                    
                    if (lastModifiedHeader) {
                        const parsedDate = new Date(lastModifiedHeader).getTime();
                        if (!isNaN(parsedDate)) {
                            serverTimestamp = parsedDate;
                            if (window.HyperLog) {
                                window.HyperLog.debug('[FileLoader] Using server timestamp from Last-Modified', { 
                                    lastModified: lastModifiedHeader,
                                    timestamp: serverTimestamp
                                });
                            }
                        }
                    }
                    
                    // Attach timestamp to result for cache management
                    const resultWithTimestamp = {
                        files: result,
                        serverTimestamp: serverTimestamp,
                        hasOtAccess: hasOtAccess
                    };
                    
                    // ✅ CRITICAL: Update component status AFTER HTTP request completes
                    // This ensures spinner stays visible until request is truly finished
                    if(window.ComponentLoadingStatus && url.includes('/api/files/')){
                        const lang = window.LANG || window.LANG_FROM_SERVER || 'EN';
                        const caption = (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function')
                            ? window.TranslationRegistry.translate('loader_stage_files_loaded', lang)
                            : 'Files loaded from server';
                        window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                    }
                    
                    if (onSuccess) {
                        onSuccess(resultWithTimestamp);
                    }
                    
                    resolve(resultWithTimestamp);
                    
                } catch (error) {
                    if (window.HyperLog) {
                        window.HyperLog.error('[FileLoader] Response processing failed', { 
                            error: error.message,
                            stack: error.stack
                        });
                    }
                    
                    // ✅ CRITICAL: Update component status even on error (request completed)
                    if(window.ComponentLoadingStatus && url.includes('/api/files/')){
                        let caption = 'Files loaded (with error)';
                        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                          const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                          caption = window.TranslationRegistry.translate('loader_stage_files_loaded_with_error', lang);
                        }
                        window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                    }
                    
                    if (onError) {
                        onError(error);
                    }
                    
                    reject(error);
                }
            })
            .fail((xhr, status, error) => {
                this.loading = false;
                
                if (window.HyperLog) {
                    window.HyperLog.error('[FileLoader] AJAX request failed', { 
                        status: xhr.status,
                        statusText: xhr.statusText,
                        error: error
                    });
                }
                
                // ✅ CRITICAL: Update component status even on error (request completed)
                if(window.ComponentLoadingStatus && url.includes('/api/files/')){
                    let caption = 'Files loaded (with error)';
                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                      const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                      caption = window.TranslationRegistry.translate('loader_stage_files_loaded_with_error', lang);
                    }
                    window.ComponentLoadingStatus.update('filesStore', 'ready', caption);
                }
                
                const apiError = new Error(`Failed to load files: ${xhr.status} ${xhr.statusText || error}`);
                
                if (onError) {
                    onError(apiError);
                }
                
                reject(apiError);
            });
        });
    }
    
    /**
     * Process server response and decompress data
     * @param {*} response - Raw server response
     * @returns {Array} - Processed files array
     */
    processResponse(response) {
        let filesList = [];
        
        try {
            if (typeof response === 'string') {
                // First, try to parse JSON (new API returns JSON string)
                try {
                    // CRITICAL: Parse JSON explicitly to ensure UTF-8 handling
                    // JSON.parse() in JavaScript handles UTF-8 correctly by default
                    const obj = JSON.parse(response);
                    if (obj && Array.isArray(obj.files)) {
                        filesList = obj.files;
                    } else if (Array.isArray(obj)) {
                        // Direct array response
                        filesList = obj;
                    } else {
                        throw new Error('not JSON files');
                    }
                } catch(e) {
                    // Fallback: treat as legacy compressed payload
                    if (window.HyperLog) {
                        window.HyperLog.debug('[FileLoader] Processing compressed string response', { length: response.length, error: e.message });
                    }
                    // Store compressed data in localStorage for caching
                    try { window.localStorage.setItem('filesList', response); } catch(_) {}
                    // Decompress for current use
                    filesList = unzip(response);
                    // Response decompression logging only in debug mode
                }

            } else if (Array.isArray(response)) {
                // Direct array response (fallback)
                filesList = response;
                if (window.HyperLog) { window.HyperLog.debug('[FileLoader] Using direct array response', { filesCount: filesList.length }); }

            } else if (response && typeof response === 'object') {
                // New API: JSON object with files array
                if (Array.isArray(response.files)) {
                    filesList = response.files;
                    // Using object.files response logging only in debug mode
                } else {
                    filesList = [];
                }
            } else {
                // Unexpected format
                filesList = [];
            }
            
        } catch (error) {
            
            // Clear corrupted localStorage
            for (const [key, value] of Object.entries(window.localStorage)) {
                window.localStorage.removeItem(key);
            }
            
            filesList = [];
        }
        
        return Array.isArray(filesList) ? filesList : [];
    }
    
    /**
     * Load files with automatic caching and validation
     * @param {Object} options - Loading options
     * @returns {Promise<Object>} - Promise with files and metadata
     */
    async loadWithCache(options = {}) {
        const {
            url = this.defaultUrl,
            forceUpdate = false,
            validateCache = true,
            onProgress = null
        } = options;
        
        try {
            const result = await this.loadFromServer({
                url: url,
                onProgress: onProgress,
                forceUpdate: forceUpdate
            });
            
            // Use server timestamp from Last-Modified header (like old main.js)
            const serverTimestamp = result.serverTimestamp || Date.now();
            localStorage.setItem('filesDate', serverTimestamp.toString());
            
            return {
                files: result.files,
                timestamp: serverTimestamp,
                source: 'server',
                cached: true,
                hasOtAccess: !!result.hasOtAccess
            };
            
        } catch (error) {
            if (window.HyperLog) {
                window.HyperLog.error('[FileLoader] Load with cache failed', { 
                    error: error.message 
                });
            }
            
            throw error;
        }
    }
    
    /**
     * Cancel current loading operation
     */
    cancel() {
        if (this.loading && this.lastRequest) {
            this.lastRequest.abort();
            this.loading = false;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[FileLoader] Request cancelled');
            }
        }
    }
    
    /**
     * Check if loader is currently loading
     * @returns {boolean}
     */
    isLoading() {
        return this.loading;
    }

    /**
     * Load summaries JSON with Last-Modified tracking.
     * Returns { summaries, serverTimestamp, fromServer }
     */
    async loadSummaries(options = {}) {
        let {
            url = this.summaryUrl,
            async = true,
            timeout = 30000,
            onSuccess = null,
            onError = null
        } = options;

        // Protect API URL from lang routing
        if (url && url.includes('/api/')) {
            url = url.replace(/^.*?(\/api\/.*)$/, '$1');
            if (!url.startsWith('/')) url = '/' + url;
        }

        return new Promise((resolve, reject) => {
            // Enable browser cache for versioned URLs (version in URL ensures freshness)
            $.ajax({ url, type: 'GET', async, timeout, cache: true })
                .done((response, status, xhr) => {
                    try {
                        const lastModifiedHeader = xhr.getResponseHeader('Last-Modified');
                        let serverTimestamp = Date.now();
                        if (lastModifiedHeader) {
                            const parsed = new Date(lastModifiedHeader).getTime();
                            if (!isNaN(parsed)) serverTimestamp = parsed;
                        }
                        let json;
                        if (typeof response === 'string') {
                            try { json = JSON.parse(response); } catch (_) { json = {}; }
                        } else {
                            json = response || {};
                        }
                        const result = { summaries: json, serverTimestamp, fromServer: true };
                        if (onSuccess) onSuccess(result);
                        resolve(result);
                    } catch (e) {
                        if (onError) onError(e);
                        reject(e);
                    }
                })
                .fail((xhr, status, error) => {
                    const apiError = new Error(`Failed to load summaries: ${xhr.status} ${xhr.statusText || error}`);
                    if (onError) onError(apiError);
                    reject(apiError);
                });
        });
    }
}

// Create singleton instance
const fileLoader = new FileLoader();

// Export for use in other modules
export { fileLoader };

// Global export for backward compatibility
window.fileLoader = fileLoader;

// Register with migration controller
//         'loadFromServer',
//         'loadWithCache', 
//         'processResponse',
//         'cancel',
//         'isLoading'
    
//     // Mark as migrated from main.js
// }

// Module registration logging only in debug mode 